import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../Icon/Icon";
import InputDatePicker from "../Inputs/InputDatePicker";
import "../../blocks/CommsList/CommsList.scss";

interface ICommunicationsGrid {
  arrData: any[] | undefined;
  recordType?: string | number;
}

const PAGE_SIZE = 5;

type TabId = "todos" | "pendientes" | "respondidos";

const TABS: { id: TabId; label: string }[] = [
  { id: "todos",       label: "Todos" },
  { id: "pendientes",  label: "Pendientes" },
  { id: "respondidos", label: "Respondidos" },
];

const formatDateShort = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
};

const CommunicationsGrid: React.FC<ICommunicationsGrid> = ({ arrData, recordType }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab]     = useState<TabId>("todos");
  const [page, setPage]               = useState(1);
  const [search, setSearch]           = useState("");
  const [onlyAttached, setOnlyAttached] = useState(false);
  const [dateFilter, setDateFilter]   = useState<string | null>(null);

  const handleTabChange = (id: TabId) => { setActiveTab(id); setPage(1); };

  const enriched = (arrData || []).map((item: any) => {
    const kind = item.puedeResponder
      ? "warn"
      : item.comunicacionReferenciaId
      ? "success"
      : "info";
    const icon = item.puedeResponder
      ? "alert"
      : item.comunicacionReferenciaId
      ? "check"
      : "chat";
    const sender = item.autorUsuario ? "Usted" : item.nombreAutor || "";
    return { ...item, kind, icon, sender };
  });

  const q = search.trim().toLowerCase();

  const matchesDate = (item: any): boolean => {
    if (!dateFilter) return true;
    if (!item.created) return false;
    const d = new Date(item.created);
    const [dd, mm, yyyy] = dateFilter.split('/');
    return (
      d.getFullYear() === parseInt(yyyy) &&
      d.getMonth() === parseInt(mm) - 1 &&
      d.getDate() === parseInt(dd)
    );
  };

  const applyCommonFilters = (items: any[]) =>
    items.filter((item) => {
      if (onlyAttached && !(item.files?.length > 0)) return false;
      if (!matchesDate(item)) return false;
      if (!q) return true;
      return (
        item.asunto?.toLowerCase().includes(q) ||
        item.sender?.toLowerCase().includes(q)
      );
    });

  const counts: Record<TabId, number> = {
    todos:       applyCommonFilters(enriched).length,
    pendientes:  applyCommonFilters(enriched.filter((item) => item.puedeResponder)).length,
    respondidos: applyCommonFilters(enriched.filter((item) => !!item.comunicacionReferenciaId)).length,
  };

  const tabFiltered = enriched.filter((item) => {
    if (activeTab === "pendientes")  return item.puedeResponder;
    if (activeTab === "respondidos") return !!item.comunicacionReferenciaId;
    return true;
  });

  const filtered   = applyCommonFilters(tabFiltered);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paged      = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleClick = (item: any) => {
    navigate(`/historial/${recordType}_${item.prestacionId}_${item.id}/comunicacion`, {
      state: { subject: "Detalle", communicationId: item.id },
    });
  };

  return (
    <div className="comms-list">
      <div className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tabs__tab${activeTab === tab.id ? " is-active" : ""}`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
            {counts[tab.id] > 0 && (
              <span className={`tabs__count${tab.id === "pendientes" && counts.pendientes > 0 ? " tabs__count--warn" : ""}`}>
                {counts[tab.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="comms-filters">
        <div className="comms-search">
          <Icon name="search" size={16} />
          <input
            className="comms-search__input"
            type="text"
            placeholder="Buscar por asunto o remitente…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            autoComplete="off"
            spellCheck={false}
          />
          {search && (
            <button
              className="comms-search__clear"
              onClick={() => { setSearch(""); setPage(1); }}
              aria-label="Limpiar búsqueda"
            >
              <Icon name="x" size={14} />
            </button>
          )}
        </div>
        <button
          className={`comms-filter-chip${onlyAttached ? " is-active" : ""}`}
          onClick={() => { setOnlyAttached((v) => !v); setPage(1); }}
        >
          <Icon name="paperclip" size={14} />
          Con adjuntos
        </button>
        <div className="comms-date-filter">
          <InputDatePicker
            id="commsgrid-date"
            name="commsgrid-date"
            placeholder="Filtrar por fecha"
            value={dateFilter}
            onChange={(val) => { setDateFilter(val); setPage(1); }}
          />
          {dateFilter && (
            <button
              className="comms-search__clear comms-date-filter__clear"
              onClick={() => { setDateFilter(null); setPage(1); }}
              aria-label="Limpiar fecha"
            >
              <Icon name="x" size={14} />
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="comms-list__empty">
          {q || dateFilter || onlyAttached
            ? "Sin resultados para los filtros aplicados."
            : "No hay comunicaciones para mostrar."}
        </p>
      ) : (
        <>
          <div className="comms">
            {paged.map((item: any, i: number) => (
              <article
                key={item.id || i}
                className={`comm comm--${item.kind}`}
                onClick={() => handleClick(item)}
                tabIndex={0}
                role="button"
                aria-label={`${item.asunto}${item.puedeResponder ? ', pendiente de respuesta' : ''}`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(item); } }}
              >
                <div className="comm__icon">
                  <Icon name={item.icon} size={18} />
                </div>
                <div className="comm__content">
                  <div className="comm__title">
                    {item.puedeResponder && <span className="comm__unread" aria-label="No leído" />}
                    {item.asunto}
                  </div>
                  {item.sender && <div className="comm__sender">{item.sender}</div>}
                  <div className="comm__body">{item.mensaje}</div>
                </div>
                <div className="comm__date">
                  {item.files?.length > 0 && (
                    <span className="comm__attach-badge" title="Tiene adjuntos">
                      <Icon name="paperclip" size={13} />
                    </span>
                  )}
                  <span className="comm__date-clip">
                    <Icon name="clock" size={11} />
                    <span className="comm__date-clip-num">
                      {item.created ? formatDateShort(item.created) : "—"}
                    </span>
                  </span>
                </div>
              </article>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="comms-list__pager">
              <span className="comms-list__pager-info">
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} de {filtered.length}
              </span>
              <div className="comms-list__pager-btns">
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={safePage <= 1}
                  onClick={() => setPage(safePage - 1)}
                >
                  <Icon name="arrow-left" size={16} /> Anterior
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage(safePage + 1)}
                >
                  Siguiente <Icon name="arrow-right" size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CommunicationsGrid;
